import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Globe,
  Loader2,
  Plus,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import {
  createPageDomain,
  getPageDomainHealth,
  removePageDomain,
  usePageDomains,
  verifyPageDomain,
  type PageDomainHealth,
} from "@/hooks/use-page-domains";
import { DomainHealthPanel } from "./domain-health-panel";

export default function DomainsPage() {
  const { domains, isLoading, error, refetch } = usePageDomains();
  const [showAdd, setShowAdd] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [setupTarget, setSetupTarget] = useState<string | null>(null);
  const [activeActionDomainId, setActiveActionDomainId] = useState<string | null>(null);
  const [healthByDomainId, setHealthByDomainId] = useState<Record<string, PageDomainHealth>>({});

  const orderedDomains = useMemo(
    () => [...domains].sort((left, right) => left.domain.localeCompare(right.domain)),
    [domains],
  );

  const handleAdd = async () => {
    const domain = newDomain.trim();
    if (!domain) return;

    setActionError(null);
    setIsSubmitting(true);

    try {
      const result = await createPageDomain(domain);
      setSetupTarget(result.cnameTarget);
      setShowAdd(false);
      setNewDomain("");
      await refetch();
    } catch {
      setActionError("Failed to register the mounted custom domain.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (domainId: string) => {
    setActionError(null);
    setActiveActionDomainId(domainId);

    try {
      await verifyPageDomain(domainId);
      await refetch();
    } catch {
      setActionError("Failed to verify DNS records for this domain.");
    } finally {
      setActiveActionDomainId(null);
    }
  };

  const handleRemove = async (domainId: string) => {
    setActionError(null);
    setActiveActionDomainId(domainId);

    try {
      await removePageDomain(domainId);
      await refetch();
    } catch {
      setActionError("Failed to remove this mounted custom domain.");
    } finally {
      setActiveActionDomainId(null);
    }
  };

  const handleScanHealth = async (domainId: string) => {
    setActionError(null);
    setActiveActionDomainId(domainId);

    try {
      const health = await getPageDomainHealth(domainId);
      setHealthByDomainId((current) => ({ ...current, [domainId]: health }));
    } catch {
      setActionError("Failed to load DNS scanner health for this domain.");
    } finally {
      setActiveActionDomainId(null);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Custom Domains</h2>
          <p className="text-sm text-muted-foreground">
            This page now manages the mounted `/crm/pages/domains` lifecycle instead of a local shell.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Add domain
        </button>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {actionError ? (
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 text-sm text-red-700">
          {actionError}
        </div>
      ) : null}

      {setupTarget ? (
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
          <p className="text-sm font-medium text-foreground">Mounted domain registration created.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Point your CNAME record at <span className="font-mono text-foreground">{setupTarget}</span>, then run verification from this page.
          </p>
        </div>
      ) : null}

      {showAdd ? (
        <div className="rounded-md border border-border p-4 space-y-4">
          <div>
            <label htmlFor="domain" className="block text-sm font-medium text-foreground">Domain name</label>
            <input
              id="domain"
              type="text"
              value={newDomain}
              onChange={(event) => setNewDomain(event.target.value)}
              placeholder="www.mybusiness.com"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="rounded-md bg-muted p-3">
            <p className="text-sm font-medium text-foreground">DNS Configuration</p>
            <p className="mt-1 text-xs text-muted-foreground">
              The mounted domain route returns a canonical CNAME target. Register the domain first, point the CNAME there, then verify from this workspace.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => { void handleAdd(); }}
              disabled={isSubmitting || !newDomain.trim()}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Add domain now
            </button>
            <button
              onClick={() => {
                setShowAdd(false);
                setNewDomain("");
              }}
              className="rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-accent"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : orderedDomains.length === 0 ? (
        <div className="rounded-md border border-border p-8 text-center">
          <Globe className="mx-auto h-10 w-10 text-muted-foreground/60" />
          <p className="mt-3 text-foreground font-medium">No custom domains yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Your site is still available on the Stero subdomain. Add a custom domain here when the brand is ready.
          </p>
        </div>
      ) : (
        <div className="rounded-md border border-border divide-y divide-border">
          {orderedDomains.map((domain) => {
            const health = healthByDomainId[domain.id];

            return (
            <div key={domain.id} className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                {domain.status === "active" ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : domain.status === "failed" ? (
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                ) : (
                  <Loader2 className="h-4 w-4 text-amber-600" />
                )}

                <div>
                  <p className="font-medium text-foreground">{domain.domain}</p>
                  <p className="text-xs text-muted-foreground">
                    Status: {domain.status} · SSL: {domain.sslStatus ?? "pending"}
                  </p>
                  {health ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      DNS scanner: {health.dnsResolved ? "resolved" : "not resolved"}
                      {health.scan ? ` · Health score ${health.scan.healthScore}/100` : ""}
                      {" · Last checked "}{new Date(health.lastChecked).toLocaleString()}
                    </p>
                  ) : null}
                  {health?.scan ? <DomainHealthPanel scan={health.scan} /> : null}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => { void handleScanHealth(domain.id); }}
                  disabled={activeActionDomainId === domain.id}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs text-foreground hover:bg-accent disabled:opacity-50"
                >
                  {activeActionDomainId === domain.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                  Scan DNS
                </button>

                {domain.status !== "active" ? (
                  <button
                    onClick={() => { void handleVerify(domain.id); }}
                    disabled={activeActionDomainId === domain.id}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs text-foreground hover:bg-accent disabled:opacity-50"
                  >
                    {activeActionDomainId === domain.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                    Verify DNS
                  </button>
                ) : null}

                <button
                  onClick={() => { void handleRemove(domain.id); }}
                  disabled={activeActionDomainId === domain.id}
                  className="inline-flex items-center gap-1.5 rounded-md border border-destructive px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
                >
                  <Trash2 className="h-3 w-3" />
                  Remove
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
